import { isOfflineQueueApp, type OfflineQueueApp } from "~/helpers/offline/queueMeta";

export const SW_MESSAGE_TYPE = {
	DISCARD_QUEUE_ENTRY: "DISCARD_QUEUE_ENTRY",
	DRAIN_QUEUE_NOW: "DRAIN_QUEUE_NOW",
	PURGE_READ_CACHE: "PURGE_READ_CACHE",
	QUEUE_REPLAY_COMPLETE: "QUEUE_REPLAY_COMPLETE",
	QUEUE_REPLAY_DISCARDED: "QUEUE_REPLAY_DISCARDED",
	QUEUE_REPLAY_ENQUEUED: "QUEUE_REPLAY_ENQUEUED",
	QUEUE_REPLAY_FAILED: "QUEUE_REPLAY_FAILED",
	QUEUE_REPLAY_STARTED: "QUEUE_REPLAY_STARTED",
	QUEUE_REPLAY_STATE_SNAPSHOT: "QUEUE_REPLAY_STATE_SNAPSHOT",
	QUEUE_REPLAY_SUCCESS: "QUEUE_REPLAY_SUCCESS",
	QUEUE_SNAPSHOT_UNAVAILABLE: "QUEUE_SNAPSHOT_UNAVAILABLE",
	READ_CACHE_SERVED: "READ_CACHE_SERVED",
	READ_CACHE_UPDATED: "READ_CACHE_UPDATED",
	REQUEST_QUEUE_STATE: "REQUEST_QUEUE_STATE",
	REQUEST_SYNC_ACTIVITY: "REQUEST_SYNC_ACTIVITY",
	REQUEST_SYNC_MODE: "REQUEST_SYNC_MODE",
	RETRY_QUEUE_ENTRY: "RETRY_QUEUE_ENTRY",
	SKIP_WAITING: "SKIP_WAITING",
	SYNC_ACTIVITY: "SYNC_ACTIVITY",
	SYNC_MODE: "SYNC_MODE"
} as const;

export type SwMessageType = (typeof SW_MESSAGE_TYPE)[keyof typeof SW_MESSAGE_TYPE];

export const QUEUE_ENTRY_STATUS = {
	FAILED: "failed",
	PENDING: "pending",
	POSTED: "posted",
	RETRYING: "retrying"
} as const;

export type QueueEntryStatus = (typeof QUEUE_ENTRY_STATUS)[keyof typeof QUEUE_ENTRY_STATUS];

export const QUEUE_ENTRY_STATUSES = Object.values(QUEUE_ENTRY_STATUS) as QueueEntryStatus[];

export const isQueueEntryStatus = (value: unknown): value is QueueEntryStatus =>
	typeof value === "string" && (QUEUE_ENTRY_STATUSES as string[]).includes(value);

export const QUEUE_FAILURE_REASON_KIND = {
	AUTH: "auth",
	BUSINESS: "business",
	NETWORK: "network",
	TRANSIENT: "transient"
} as const;

export type QueueFailureReasonKind = (typeof QUEUE_FAILURE_REASON_KIND)[keyof typeof QUEUE_FAILURE_REASON_KIND];

export const QUEUE_FAILURE_REASON_KINDS = Object.values(QUEUE_FAILURE_REASON_KIND) as QueueFailureReasonKind[];

export const isQueueFailureReasonKind = (value: unknown): value is QueueFailureReasonKind =>
	typeof value === "string" && (QUEUE_FAILURE_REASON_KINDS as string[]).includes(value);

// Background Sync is Chromium-only, so every other browser resolves to foreground-only.
export const SYNC_MODE = {
	BACKGROUND_SYNC: "background-sync",
	FOREGROUND_ONLY: "foreground-only"
} as const;

export type SyncMode = (typeof SYNC_MODE)[keyof typeof SYNC_MODE];

export const SYNC_MODES = Object.values(SYNC_MODE) as SyncMode[];

export const isSyncMode = (value: unknown): value is SyncMode =>
	typeof value === "string" && (SYNC_MODES as string[]).includes(value);

export const SYNC_ACTIVITY_KIND = {
	REGISTRATION_FAILED: "registration-failed",
	REGISTRATION_SUCCEEDED: "registration-succeeded",
	SYNC_EVENT_RECEIVED: "sync-event-received"
} as const;

export type SyncActivityKind = (typeof SYNC_ACTIVITY_KIND)[keyof typeof SYNC_ACTIVITY_KIND];

export const SYNC_ACTIVITY_KINDS = Object.values(SYNC_ACTIVITY_KIND) as SyncActivityKind[];

export const isSyncActivityKind = (value: unknown): value is SyncActivityKind =>
	typeof value === "string" && (SYNC_ACTIVITY_KINDS as string[]).includes(value);

export interface SyncActivityRecord {
	at: number;
	detail?: string;
	kind: SyncActivityKind;
	windowCount?: number;
}

export const isSyncActivityRecord = (value: unknown): value is SyncActivityRecord => {
	if (!value || typeof value !== "object") return false;
	const { at, detail, kind, windowCount } = value as Record<string, unknown>;
	if (typeof at !== "number" || !Number.isFinite(at)) return false;
	if (!isSyncActivityKind(kind)) return false;
	if (detail !== undefined && typeof detail !== "string") return false;
	return windowCount === undefined || (typeof windowCount === "number" && Number.isFinite(windowCount));
};

export const SYNC_MODE_REASON = {
	PERMISSION_DENIED: "permission-denied",
	REGISTRATION_FAILED: "registration-failed",
	UNSUPPORTED: "unsupported"
} as const;

export type SyncModeReason = (typeof SYNC_MODE_REASON)[keyof typeof SYNC_MODE_REASON];

export const SYNC_MODE_REASONS = Object.values(SYNC_MODE_REASON) as SyncModeReason[];

export const isSyncModeReason = (value: unknown): value is SyncModeReason =>
	typeof value === "string" && (SYNC_MODE_REASONS as string[]).includes(value);

// A page can be controlled by a previously installed worker, so everything past clientId is wire-optional.
export interface QueueEntryDescriptor {
	app?: OfflineQueueApp;
	attempts?: number;
	clientId: string;
	detail?: string;
	enqueuedAt?: number;
	labelKey?: string;
	lastAttemptAt?: number;
	mutationName: string;
	reconciliationId?: string;
	refusal?: QueueRefusal;
	status?: QueueEntryStatus;
}

export interface QueueReplayStartedMessage {
	type: typeof SW_MESSAGE_TYPE.QUEUE_REPLAY_STARTED;
}

export interface QueueReplayCompleteMessage {
	type: typeof SW_MESSAGE_TYPE.QUEUE_REPLAY_COMPLETE;
}

export interface QueueReplayEnqueuedMessage extends QueueEntryDescriptor {
	type: typeof SW_MESSAGE_TYPE.QUEUE_REPLAY_ENQUEUED;
}

export interface QueueReplaySuccessMessage extends QueueEntryDescriptor {
	// Absent for older SW builds and non-FTE mutations — consumers treat
	// absence as "no reconciliation required", not an error.
	floatingTimeEntryId?: string;
	type: typeof SW_MESSAGE_TYPE.QUEUE_REPLAY_SUCCESS;
}

export interface QueueRefusal {
	code?: string;
	message: string;
}

export interface QueueReplayFailedMessage extends QueueEntryDescriptor {
	reason: string;
	reasonKind?: QueueFailureReasonKind;
	retryable?: boolean;
	type: typeof SW_MESSAGE_TYPE.QUEUE_REPLAY_FAILED;
}

export interface QueueReplayDiscardedMessage extends QueueEntryDescriptor {
	type: typeof SW_MESSAGE_TYPE.QUEUE_REPLAY_DISCARDED;
}

export interface QueueReplayStateSnapshotMessage {
	entries: QueueEntryDescriptor[];
	type: typeof SW_MESSAGE_TYPE.QUEUE_REPLAY_STATE_SNAPSHOT;
}

// The worker's read of its own queue failed — the client must keep treating the queue's contents as unknown.
export interface QueueSnapshotUnavailableMessage {
	type: typeof SW_MESSAGE_TYPE.QUEUE_SNAPSHOT_UNAVAILABLE;
}

// ageMs is absent when the cached entry predates the freshness stamp.
export interface ReadCacheServedMessage {
	ageMs?: number;
	cacheKey?: string;
	operationName: string;
	type: typeof SW_MESSAGE_TYPE.READ_CACHE_SERVED;
}

export interface ReadCacheUpdatedMessage {
	operationName: string;
	type: typeof SW_MESSAGE_TYPE.READ_CACHE_UPDATED;
}

export interface SyncActivityMessage {
	records: SyncActivityRecord[];
	type: typeof SW_MESSAGE_TYPE.SYNC_ACTIVITY;
}

export interface SyncModeMessage {
	mode: SyncMode;
	reason?: SyncModeReason;
	type: typeof SW_MESSAGE_TYPE.SYNC_MODE;
}

export type SwOutboundMessage =
	| QueueReplayCompleteMessage
	| QueueReplayDiscardedMessage
	| QueueReplayEnqueuedMessage
	| QueueReplayFailedMessage
	| QueueReplayStartedMessage
	| QueueReplayStateSnapshotMessage
	| QueueReplaySuccessMessage
	| QueueSnapshotUnavailableMessage
	| ReadCacheServedMessage
	| ReadCacheUpdatedMessage
	| SyncActivityMessage
	| SyncModeMessage;

export interface RequestQueueStateMessage {
	type: typeof SW_MESSAGE_TYPE.REQUEST_QUEUE_STATE;
}

export interface RequestSyncActivityMessage {
	type: typeof SW_MESSAGE_TYPE.REQUEST_SYNC_ACTIVITY;
}

export interface RequestSyncModeMessage {
	type: typeof SW_MESSAGE_TYPE.REQUEST_SYNC_MODE;
}

export interface SkipWaitingMessage {
	type: typeof SW_MESSAGE_TYPE.SKIP_WAITING;
}

export interface DrainQueueNowMessage {
	type: typeof SW_MESSAGE_TYPE.DRAIN_QUEUE_NOW;
}

export interface RetryQueueEntryMessage {
	clientId: string;
	locationId?: string;
	type: typeof SW_MESSAGE_TYPE.RETRY_QUEUE_ENTRY;
}

export interface DiscardQueueEntryMessage {
	clientId: string;
	type: typeof SW_MESSAGE_TYPE.DISCARD_QUEUE_ENTRY;
}

export interface PurgeReadCacheMessage {
	type: typeof SW_MESSAGE_TYPE.PURGE_READ_CACHE;
}

// Wire shape is the flat {title, body, url} from wsapi-services NotificationCopy.cs; nested data.url is also accepted; url is optional.
export interface PushNotificationPayload {
	body: string;
	data?: { url?: string | null };
	title: string;
	url?: string | null;
}

const isOptionalString = (value: unknown): boolean => value === undefined || typeof value === "string";

export const isPushNotificationPayload = (value: unknown): value is PushNotificationPayload => {
	if (!value || typeof value !== "object") return false;
	const { body, data, title, url } = value as Record<string, unknown>;
	if (typeof title !== "string" || typeof body !== "string") return false;
	if (!isOptionalString(url ?? undefined)) return false;
	if (!data || typeof data !== "object") return true;
	return isOptionalString((data as Record<string, unknown>).url ?? undefined);
};

export type SwInboundMessage =
	| DiscardQueueEntryMessage
	| DrainQueueNowMessage
	| PurgeReadCacheMessage
	| RequestQueueStateMessage
	| RequestSyncActivityMessage
	| RequestSyncModeMessage
	| RetryQueueEntryMessage
	| SkipWaitingMessage;

const isOptionalNumber = (value: unknown): boolean => value === undefined || typeof value === "number";

export const isQueueRefusal = (value: unknown): value is QueueRefusal => {
	if (!value || typeof value !== "object") return false;
	const { code, message } = value as Record<string, unknown>;
	return typeof message === "string" && message.length > 0 && isOptionalString(code);
};

const hasValidDescriptorFields = (data: unknown): boolean => {
	const {
		app,
		attempts,
		clientId,
		detail,
		enqueuedAt,
		labelKey,
		lastAttemptAt,
		mutationName,
		reconciliationId,
		refusal,
		status
	} = data as Record<string, unknown>;
	if (typeof clientId !== "string" || typeof mutationName !== "string") return false;
	if (refusal !== undefined && !isQueueRefusal(refusal)) return false;
	if (app !== undefined && !isOfflineQueueApp(app)) return false;
	if (status !== undefined && !isQueueEntryStatus(status)) return false;
	if (!isOptionalNumber(attempts) || !isOptionalNumber(enqueuedAt) || !isOptionalNumber(lastAttemptAt)) return false;
	return isOptionalString(labelKey) && isOptionalString(reconciliationId) && isOptionalString(detail);
};

const PER_ENTRY_TYPES: SwMessageType[] = [
	SW_MESSAGE_TYPE.QUEUE_REPLAY_DISCARDED,
	SW_MESSAGE_TYPE.QUEUE_REPLAY_ENQUEUED,
	SW_MESSAGE_TYPE.QUEUE_REPLAY_FAILED,
	SW_MESSAGE_TYPE.QUEUE_REPLAY_SUCCESS
];

export const isSwInboundMessage = (data: unknown): data is SwInboundMessage => {
	if (!data || typeof data !== "object") return false;
	const type = (data as { type?: unknown }).type;
	if (
		type === SW_MESSAGE_TYPE.SKIP_WAITING ||
		type === SW_MESSAGE_TYPE.REQUEST_QUEUE_STATE ||
		type === SW_MESSAGE_TYPE.REQUEST_SYNC_ACTIVITY ||
		type === SW_MESSAGE_TYPE.REQUEST_SYNC_MODE ||
		type === SW_MESSAGE_TYPE.DRAIN_QUEUE_NOW ||
		type === SW_MESSAGE_TYPE.PURGE_READ_CACHE
	) {
		return true;
	}
	if (type !== SW_MESSAGE_TYPE.RETRY_QUEUE_ENTRY && type !== SW_MESSAGE_TYPE.DISCARD_QUEUE_ENTRY) return false;
	const { clientId, locationId } = data as { clientId?: unknown; locationId?: unknown };
	return typeof clientId === "string" && clientId.length > 0 && isOptionalString(locationId);
};

const isSwOutboundMessage = (data: unknown): data is SwOutboundMessage => {
	if (!data || typeof data !== "object") return false;
	const type = (data as { type?: unknown }).type;
	if (type === SW_MESSAGE_TYPE.QUEUE_REPLAY_STARTED) return true;
	if (type === SW_MESSAGE_TYPE.QUEUE_REPLAY_COMPLETE) return true;
	if (type === SW_MESSAGE_TYPE.QUEUE_SNAPSHOT_UNAVAILABLE) return true;
	if (type === SW_MESSAGE_TYPE.SYNC_ACTIVITY) {
		const records = (data as { records?: unknown }).records;
		return Array.isArray(records) && records.every(isSyncActivityRecord);
	}
	if (type === SW_MESSAGE_TYPE.SYNC_MODE) {
		const { mode, reason } = data as Record<string, unknown>;
		if (!isSyncMode(mode)) return false;
		return reason === undefined || isSyncModeReason(reason);
	}
	if (type === SW_MESSAGE_TYPE.READ_CACHE_SERVED) {
		const { ageMs, cacheKey, operationName } = data as Record<string, unknown>;
		if (typeof operationName !== "string" || operationName.length === 0) return false;
		return isOptionalNumber(ageMs) && isOptionalString(cacheKey);
	}
	if (type === SW_MESSAGE_TYPE.READ_CACHE_UPDATED) {
		const { operationName } = data as Record<string, unknown>;
		return typeof operationName === "string" && operationName.length > 0;
	}
	if (type === SW_MESSAGE_TYPE.QUEUE_REPLAY_STATE_SNAPSHOT) {
		const entries = (data as { entries?: unknown }).entries;
		return (
			Array.isArray(entries) &&
			entries.every((entry) => entry && typeof entry === "object" && hasValidDescriptorFields(entry))
		);
	}
	if (!PER_ENTRY_TYPES.includes(type as SwMessageType)) return false;
	if (!hasValidDescriptorFields(data)) return false;
	if (type === SW_MESSAGE_TYPE.QUEUE_REPLAY_FAILED) {
		const { reason, reasonKind, retryable } = data as Record<string, unknown>;
		if (typeof reason !== "string") return false;
		if (reasonKind !== undefined && !isQueueFailureReasonKind(reasonKind)) return false;
		return retryable === undefined || typeof retryable === "boolean";
	}
	if (type === SW_MESSAGE_TYPE.QUEUE_REPLAY_SUCCESS) {
		return isOptionalString((data as { floatingTimeEntryId?: unknown }).floatingTimeEntryId);
	}
	return true;
};

export default isSwOutboundMessage;
